import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowUp, ArrowDown, Star, Trash2, Upload, X, Replace } from "lucide-react";
import { categories as portfolioCategories } from "@/Portfolio";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const KEY_STORAGE = "vc_admin_key";

export default function Manage() {
  const [key, setKey] = useState(sessionStorage.getItem(KEY_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [videos, setVideos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pForm, setPForm] = useState({ title: "", category: "print-design", client: "", year: "", description: "", featured: false });
  const projectFileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const replaceRefs = useRef({});

  const auth = { headers: { "X-Admin-Key": key } };
  const refresh = () => { axios.get(`${API}/showreel-videos?all=1`, auth).then((r) => setVideos(r.data)).catch(() => setError("Could not load videos")); axios.get(`${API}/portfolio`).then((r) => setProjects(r.data)).catch(() => {}); };

  useEffect(() => { if (key) verify(key); }, []);

  const verify = async (candidate) => {
    try {
      const list = (await axios.get(`${API}/showreel-videos?all=1`, { headers: { "X-Admin-Key": candidate } })).data;
      sessionStorage.setItem(KEY_STORAGE, candidate);
      setKey(candidate);
      setVideos(list);
      setAuthed(true);
      setError("");
    } catch {
      sessionStorage.removeItem(KEY_STORAGE);
      setError("Wrong passcode");
    }
  };

  const run = async (action) => {
    setBusy(true);
    setError("");
    try { await action(); await refresh(); }
    catch (e) { setError(e.response?.status === 401 ? "Session expired — unlock again" : (e.response?.data?.detail || "Action failed")); if (e.response?.status === 401) setAuthed(false); }
    finally { setBusy(false); }
  };

  const upload = (file, order, title) => {
    const data = new FormData();
    data.append("file", file);
    data.append("order", order);
    data.append("title", title || file.name);
    return axios.post(`${API}/showreel-videos`, data, auth);
  };

  const move = (index, dir) => run(() => {
    const ids = reels.map((v) => v.id);
    const swap = index + dir;
    [ids[index], ids[swap]] = [ids[swap], ids[index]];
    return axios.patch(`${API}/showreel-videos/reorder`, { ids }, auth);
  });

  const heroVideos = videos.filter((v) => v.hero_only);
  const reels = videos.filter((v) => !v.hero_only);

  const Thumb = ({ video }) => video.poster_path
    ? <img className="manage-thumb" src={`${API}/files/${video.poster_path}`} alt={video.title} />
    : <video className="manage-thumb" src={`${API}/files/${video.storage_path}`} muted preload="metadata" playsInline />;

  if (!authed) {
    return (
      <div className="manage-gate" data-testid="manage-gate">
        <form className="manage-card" onSubmit={(e) => { e.preventDefault(); verify(key); }}>
          <span className="manage-eyebrow">VirtualCreato Studio</span>
          <h1>Showreel Manager</h1>
          <input data-testid="manage-passcode-input" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Passcode" autoFocus />
          {error && <p className="manage-error" data-testid="manage-error">{error}</p>}
          <button data-testid="manage-unlock-button" type="submit" disabled={!key}>Unlock</button>
        </form>
      </div>
    );
  }

  return (
    <div className="manage-wrap" data-testid="manage-page">
      <header className="manage-head">
        <div><span className="manage-eyebrow">VirtualCreato Studio</span><h1>Showreel Manager</h1></div>
        <div className="manage-head-actions">
          <button className="manage-add" data-testid="manage-add-video-button" onClick={() => fileRef.current?.click()} disabled={busy}><Upload size={15} /> Add video</button>
          <button className="manage-lock" data-testid="manage-lock-button" onClick={() => { sessionStorage.removeItem(KEY_STORAGE); setKey(""); setAuthed(false); }}><X size={15} /> Lock</button>
        </div>
        <input ref={fileRef} type="file" accept="video/*" hidden data-testid="manage-upload-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) run(() => upload(f, reels.length)); e.target.value = ""; }} />
      </header>
      {error && <p className="manage-error" data-testid="manage-list-error">{error}</p>}
      {busy && <p className="manage-note">Working…</p>}
      {heroVideos.length > 0 && <h2 className="manage-sub">Hero video — homepage only, not in showreel</h2>}
      <div className="manage-list">
        {heroVideos.map((video) => (
          <div className="manage-item is-hero" data-testid="manage-hero-video" key={video.id}>
            <Thumb video={video} />
            <div className="manage-meta">
              <span className="manage-hero-title">{video.title}</span>
              <span>{video.original_filename} · {(video.size / 1048576).toFixed(1)} MB</span>
              <em data-testid="manage-hero-badge">Hero only</em>
            </div>
            <div className="manage-actions" />
          </div>
        ))}
      </div>
      <h2 className="manage-sub">Showreel videos</h2>
      <div className="manage-list">
        {reels.map((video, i) => (
          <div className={`manage-item ${video.featured ? "is-featured" : ""}`} data-testid={`manage-video-${i + 1}`} key={video.id}>
            <Thumb video={video} />
            <div className="manage-meta">
              <input data-testid={`manage-title-${i + 1}`} defaultValue={video.title || video.original_filename} onBlur={(e) => e.target.value !== (video.title || video.original_filename) && run(() => axios.patch(`${API}/showreel-videos/${video.id}`, { title: e.target.value }, auth))} />
              <span>{video.original_filename} · {(video.size / 1048576).toFixed(1)} MB</span>
              {video.featured && <em data-testid={`manage-featured-badge-${i + 1}`}>Featured</em>}
            </div>
            <div className="manage-actions">
              <button data-testid={`manage-up-${i + 1}`} aria-label="Move up" disabled={i === 0 || busy} onClick={() => move(i, -1)}><ArrowUp size={15} /></button>
              <button data-testid={`manage-down-${i + 1}`} aria-label="Move down" disabled={i === reels.length - 1 || busy} onClick={() => move(i, 1)}><ArrowDown size={15} /></button>
              <button data-testid={`manage-feature-${i + 1}`} aria-label="Set featured" disabled={video.featured || busy} onClick={() => run(() => axios.patch(`${API}/showreel-videos/${video.id}`, { featured: true }, auth))}><Star size={15} fill={video.featured ? "currentColor" : "none"} /></button>
              <button data-testid={`manage-replace-${i + 1}`} aria-label="Replace video" disabled={busy} onClick={() => replaceRefs.current[video.id]?.click()}><Replace size={15} /></button>
              <button data-testid={`manage-delete-${i + 1}`} aria-label="Delete video" disabled={busy} onClick={() => window.confirm(`Delete "${video.title || video.original_filename}"?`) && run(() => axios.delete(`${API}/showreel-videos/${video.id}`, auth))}><Trash2 size={15} /></button>
              <input ref={(el) => (replaceRefs.current[video.id] = el)} type="file" accept="video/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) run(async () => { await upload(f, video.order, video.title); await axios.delete(`${API}/showreel-videos/${video.id}`, auth); }); e.target.value = ""; }} />
            </div>
          </div>
        ))}
        {reels.length === 0 && !busy && <p className="manage-note">No showreel videos yet — use “Add video” to upload your first film.</p>}
      </div>
      <h2 className="manage-sub">Portfolio projects</h2>
      <form className="manage-form" data-testid="portfolio-form" onSubmit={(e) => { e.preventDefault(); const f = projectFileRef.current?.files?.[0]; if (!f) { setError("Choose a project image first"); return; } run(async () => { const data = new FormData(); data.append("file", f); Object.entries(pForm).forEach(([k, v]) => data.append(k, v)); await axios.post(`${API}/portfolio`, data, auth); setPForm({ title: "", category: "print-design", client: "", year: "", description: "", featured: false }); projectFileRef.current.value = ""; }); }}>
        <input type="file" accept="image/*" ref={projectFileRef} data-testid="portfolio-image-input" />
        <input placeholder="Project title" value={pForm.title} required data-testid="portfolio-title-input" onChange={(e) => setPForm({ ...pForm, title: e.target.value })} />
        <select value={pForm.category} data-testid="portfolio-category-select" onChange={(e) => setPForm({ ...pForm, category: e.target.value })}>{portfolioCategories.map((c) => <option key={c.key} value={c.key}>{c.title}</option>)}</select>
        <input placeholder="Client (optional)" value={pForm.client} data-testid="portfolio-client-input" onChange={(e) => setPForm({ ...pForm, client: e.target.value })} />
        <input placeholder="Year (optional)" value={pForm.year} data-testid="portfolio-year-input" onChange={(e) => setPForm({ ...pForm, year: e.target.value })} />
        <input placeholder="Short description (optional)" value={pForm.description} data-testid="portfolio-description-input" onChange={(e) => setPForm({ ...pForm, description: e.target.value })} />
        <label className="manage-check"><input type="checkbox" checked={pForm.featured} data-testid="portfolio-featured-check" onChange={(e) => setPForm({ ...pForm, featured: e.target.checked })} /> Featured cover</label>
        <button type="submit" className="manage-add" disabled={busy} data-testid="portfolio-add-button"><Upload size={15} /> Add project</button>
      </form>
      {portfolioCategories.map((cat) => { const items = projects.filter((p) => p.category === cat.key); if (!items.length) return null; return <div key={cat.key}><h3 className="manage-cat">{cat.title} — {items.length}</h3><div className="manage-list">{items.map((p) => <div className={`manage-item ${p.featured ? "is-featured" : ""}`} data-testid={`portfolio-item-${p.id}`} key={p.id}><img className="manage-thumb" src={`${API}/files/${p.storage_path}`} alt={p.title} /><div className="manage-meta"><span className="manage-hero-title">{p.title}</span><span>{[p.client, p.year].filter(Boolean).join(" · ") || p.original_filename}</span>{p.featured && <em>Featured</em>}</div><div className="manage-actions"><button aria-label="Set featured" disabled={p.featured || busy} onClick={() => run(() => axios.patch(`${API}/portfolio/${p.id}`, { featured: true }, auth))}><Star size={15} fill={p.featured ? "currentColor" : "none"} /></button><button aria-label="Delete project" disabled={busy} onClick={() => window.confirm(`Delete "${p.title}"?`) && run(() => axios.delete(`${API}/portfolio/${p.id}`, auth))}><Trash2 size={15} /></button></div></div>)}</div></div>; })}
      {projects.length === 0 && !busy && <p className="manage-note">No portfolio projects yet — add your first above.</p>}
    </div>
  );
}
